import React, { useCallback, useState, useMemo } from 'react';
import { makeStyles } from "@material-ui/core/styles";
import Icon from '@mdi/react';
import { mdiChevronDown, mdiChevronUp } from '@mdi/js';

const useStyles = makeStyles((theme) => ({
  legend: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    background: 'white',
    border: '1px solid #ccc',
    borderRadius: '4px',
    zIndex: 2,
    fontSize: '12px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    maxWidth: '250px',
    minWidth: '200px'
  },
  legendHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 10px',
    cursor: 'pointer',
    fontWeight: 'bold',
    borderBottom: '1px solid #eee',
    userSelect: 'none'
  },
  legendContent: {
    maxHeight: '400px',
    overflowY: 'auto'
  },
  legendCollapsed: {
    maxHeight: 0,
    overflow: 'hidden'
  },
  layerItem: {
    borderBottom: '1px solid #f0f0f0',
    '&:last-child': {
      borderBottom: 'none'
    }
  },
  layerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 10px',
    cursor: 'pointer',
    userSelect: 'none',
    gap: '8px',
    '&:hover': {
      backgroundColor: '#f5f5f5'
    }
  },
  layerTitle: {
    flex: 1,
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0
  },
  layerChevron: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center'
  },
  layerContent: {
    padding: '8px 10px',
    transition: 'max-height 0.2s ease-out'
  },
  layerContentCollapsed: {
    padding: 0,
    maxHeight: 0,
    overflow: 'hidden',
    transition: 'max-height 0.2s ease-out'
  }
}));

// Helper function to convert hex to rgba
const hexToRgba = (hex, opacity) => {
  if (!hex || typeof hex !== 'string' || !hex.startsWith('#')) {
    return hex || 'transparent';
  }
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

// Helper function to get opacity from paint properties
const getOpacity = (paint, propertyName, index) => {
  if (!paint[propertyName]) return 1;
  if (Array.isArray(paint[propertyName])) {
    return paint[propertyName][index + 1] || 1;
  }
  return typeof paint[propertyName] === 'number' ? paint[propertyName] : 1;
};

// Helper function to render match expression items
const renderMatchItems = (matchExpr, keyPrefix, renderFunc) => {
  if (!Array.isArray(matchExpr) || matchExpr[0] !== 'match') return [];
  
  const items = [];
  for (let i = 2; i < matchExpr.length; i += 2) {
    const value = matchExpr[i];
    const color = matchExpr[i + 1];
    
    if (typeof value === 'string' && !value.startsWith('#')) {
      const name = value.replace('ler:', '');
      items.push(renderFunc(keyPrefix, i, name, color));
    }
  }
  return items;
};

export default function MapLegend({ layer }) {
  const classes = useStyles();
  const [isExpanded, setIsExpanded] = useState(true);
  const [expandedLayers, setExpandedLayers] = useState({});

  const toggleLayer = useCallback((layerIndex) => {
    setExpandedLayers(prev => ({
      ...prev,
      [layerIndex]: !prev[layerIndex]
    }));
  }, []);

  const renderFillLegend = useCallback((style, keyPrefix) => {
    const { paint } = style;
    const fillColor = paint['fill-color'];
    
    // Handle solid color
    if (typeof fillColor === 'string') {
      return (
        <div key={`${keyPrefix}-visual`} className="legend-visual">
          <div className="legend-color-box" style={{ 
            backgroundColor: fillColor, 
            opacity: paint['fill-opacity'] || 1 
          }}></div>
          <span>{style.id}</span>
        </div>
      );
    }

    // Handle case expressions with nested match
    if (Array.isArray(fillColor) && fillColor[0] === 'case') {
      const items = [];
      const errorColor = fillColor[2];
      const opacity = paint['fill-opacity'] || 0.5;
      
      // Add error state
      items.push(
        <div key={`${keyPrefix}-visual-error`} className="legend-visual">
          <div className="legend-color-box" style={{ 
            backgroundColor: hexToRgba(errorColor, opacity)
          }}></div>
          <span>Fejl i data!</span>
        </div>
      );

      // Add match items
      const matchExpr = fillColor[3];
      items.push(...renderMatchItems(matchExpr, keyPrefix, (prefix, i, name, color) => (
        <div key={`${prefix}-visual-${i}`} className="legend-visual">
          <div className="legend-color-box" style={{ 
            backgroundColor: hexToRgba(color, opacity)
          }}></div>
          <span>{name}</span>
        </div>
      )));
      
      return items;
    }

    // Handle match expressions (skip first 2 elements: 'match' and property accessor)
    if (!Array.isArray(fillColor)) return [];
    
    const items = [];
    for (let i = 2; i < fillColor.length; i += 2) {
      const value = fillColor[i];
      const color = fillColor[i + 1];
      
      if (!value || !color) continue;
      
      const name = typeof value === 'string' && value.startsWith('#') ? 'Andet' : value;
      const opacity = getOpacity(paint, 'fill-opacity', i);
      const rgbaColor = hexToRgba(color, opacity);

      items.push(
        <div key={`${keyPrefix}-visual-${i}`} className="legend-visual">
          <div className="legend-color-box" style={{ backgroundColor: rgbaColor }}></div>
          <span>{name}</span>
        </div>
      );
    }
    return items;
  }, []);

  const renderLineLegend = useCallback((style, keyPrefix) => {
    const { paint } = style;
    const lineColor = paint['line-color'];
    
    // Handle solid color
    if (typeof lineColor === 'string') {
      const width = paint['line-width'] || 2;
      return (
        <div key={`${keyPrefix}-visual`} className="legend-visual">
          <div className="legend-line-box" style={{ 
            backgroundColor: lineColor, 
            height: `${typeof width === 'number' ? width : 2}px`,
            width: '30px'
          }}></div>
          <span>{style.id}</span>
        </div>
      );
    }

    // Handle case expressions with nested match
    if (!Array.isArray(lineColor) || lineColor[0] !== 'case') return [];
    
    const items = [];
    const errorColor = lineColor[2];
    
    // Add error state
    items.push(
      <div key={`${keyPrefix}-visual-error`} className="legend-visual">
        <div className="legend-line-box" style={{ 
          backgroundColor: errorColor,
          height: '6px',
          width: '30px'
        }}></div>
        <span>Fejl i data!</span>
      </div>
    );

    // Add match items
    const matchExpr = lineColor[3];
    items.push(...renderMatchItems(matchExpr, keyPrefix, (prefix, i, name, color) => (
      <div key={`${prefix}-visual-${i}`} className="legend-visual">
        <div className="legend-line-box" style={{ 
          backgroundColor: color,
          height: '3px',
          width: '30px'
        }}></div>
        <span>{name}</span>
      </div>
    )));
    
    return items;
  }, []);

  const renderCircleLegend = useCallback((style, keyPrefix) => {
    const { paint } = style;
    const circleColor = paint['circle-color'];
    const strokeColor = paint['circle-stroke-color'] || '#ffffff';
    const strokeWidth = paint['circle-stroke-width'] || 0;
    
    // Handle solid color
    if (typeof circleColor === 'string') {
      const radius = paint['circle-radius'] || 5;
      const size = typeof radius === 'number' ? radius * 2 : 10;
      
      return (
        <div key={`${keyPrefix}-visual`} className="legend-visual">
          <div style={{ 
            backgroundColor: circleColor,
            border: `${strokeWidth}px solid ${strokeColor}`,
            borderRadius: '50%',
            height: `${size}px`,
            width: `${size}px`,
            display: 'inline-block',
            marginRight: '8px'
          }}></div>
          <span>{style.id}</span>
        </div>
      );
    }

    // Handle case expressions with nested match
    if (!Array.isArray(circleColor) || circleColor[0] !== 'case') return [];
    
    const items = [];
    const errorColor = circleColor[2];
    
    // Add error state
    items.push(
      <div key={`${keyPrefix}-visual-error`} className="legend-visual">
        <div style={{ 
          backgroundColor: errorColor,
          border: `${strokeWidth}px solid ${strokeColor}`,
          borderRadius: '50%',
          height: '16px',
          width: '16px',
          display: 'inline-block',
          marginRight: '8px'
        }}></div>
        <span>Fejl i data!</span>
      </div>
    );

    // Add match items
    const matchExpr = circleColor[3];
    items.push(...renderMatchItems(matchExpr, keyPrefix, (prefix, i, name, color) => (
      <div key={`${prefix}-visual-${i}`} className="legend-visual">
        <div style={{ 
          backgroundColor: color,
          border: `${strokeWidth}px solid ${strokeColor}`,
          borderRadius: '50%',
          height: '10px',
          width: '10px',
          display: 'inline-block',
          marginRight: '8px'
        }}></div>
        <span>{name}</span>
      </div>
    )));
    
    return items;
  }, []);

  const renderLegend = useCallback((style, keyPrefix) => {
    let content;
    
    switch (style.type) {
      case 'fill':
        content = renderFillLegend(style, keyPrefix);
        break;
      case 'line':
        content = renderLineLegend(style, keyPrefix);
        break;
      case 'circle':
        content = renderCircleLegend(style, keyPrefix);
        break;
      default:
        return null;
    }

    return (
      <div key={`${keyPrefix}-${style.id}`} className="legend-item">
        {content}
      </div>
    );
  }, [renderFillLegend, renderLineLegend, renderCircleLegend]);

  const legendContent = useMemo(() => {
    if (!layer) return null;
    
    return layer.map((l, layerIndex) => {
      const isLayerExpanded = expandedLayers[layerIndex] ?? true;
      const rawName = l.name || l.id || `Layer ${layerIndex + 1}`;
      const layerName = rawName.charAt(0).toUpperCase() + rawName.slice(1);
      
      return (
        <div key={`layer-${layerIndex}`} className={classes.layerItem}>
          <div 
            className={classes.layerHeader}
            onClick={() => toggleLayer(layerIndex)}
          >
            <div className={classes.layerTitle} title={layerName}>
              {layerName}
            </div>
            <div className={classes.layerChevron}>
              <Icon 
                path={isLayerExpanded ? mdiChevronUp : mdiChevronDown} 
                size={0.7}
              />
            </div>
          </div>
          <div className={isLayerExpanded ? classes.layerContent : classes.layerContentCollapsed}>
            {l.style.map((style, styleIndex) => renderLegend(style, `${layerIndex}-${styleIndex}`))}
          </div>
        </div>
      );
    });
  }, [layer, expandedLayers, renderLegend, classes, toggleLayer]);

  if (!layer) return null;

  return (
    <div className={`${classes.legend} map-legend`}>
      <div 
        className={classes.legendHeader} 
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span>Signatur</span>
        <Icon 
          path={isExpanded ? mdiChevronUp : mdiChevronDown} 
          size={0.8}
        />
      </div>
      <div className={isExpanded ? classes.legendContent : classes.legendCollapsed}>
        {legendContent}
      </div>
    </div>
  );
}
